import { BrowserRouter as Router, Route, Routes, NavLink, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import CryptoFearGreedIndex from "./CryptoFearGreedIndex";
import BitcoinDominance from "./BitcoinDominance";
import Heatmap from "./Heatmap";

const homeUrl = process.env.REACT_APP_HOME_URL;
const apiUrl = process.env.REACT_APP_API_URL;
const adminUrl = process.env.REACT_APP_ADMIN_URL;

const CACHE_KEY = 'apiData';
const CACHE_DURATION = 60 * 1000 * 4; // 4 minute
const REFRESH_THRESHOLD = 60 * 1000; // 15 seconds

// Save to cache with expiry
const setCache = (key, data, ttl) => {
  const now = Date.now();
  const expiry = now + ttl;
  const item = { data, timestamp: now, expiry };
  localStorage.setItem(key, JSON.stringify(item));
};

// Load from cache and decide if it's fresh enough
const getCache = (key) => {
  const cached = localStorage.getItem(key);
  if (!cached) return null;

  const { data, timestamp, expiry } = JSON.parse(cached);
  const now = Date.now();

  if (now > expiry) {
    localStorage.removeItem(key);
    return null;
  }

  const isStale = now - timestamp > REFRESH_THRESHOLD;

  return { data, isStale };
};

// Utility: Generate QuickChart.io chart URL
const getChartUrl = (data, label, clr = 'Data') => {
  if (!data || data.length === 0) return '';

  let chart_clr = '';
  if(clr > 0) {
  	chart_clr = 'green';
  }
  else {
  	chart_clr = 'red';
  }
  const chartData = {
    type: 'line',
    data: {
      labels: data.map((_, i) => i),
      datasets: [
        {
          label: label,
          data: data,
          borderColor: chart_clr,
          fill: false,
          borderWidth: 2,
          pointRadius: 0
        },
      ],
    },
    options: {
      elements: { line: { tension: 0.4 } },
      plugins: { legend: { display: false } },
      scales: {
        x: { display: false },
        y: { display: false },
      },
    },
  };

  const encoded = encodeURIComponent(JSON.stringify(chartData));
  return `https://quickchart.io/chart?c=${encoded}`;
};

// Utility: Format numbers with commas
const formatNumber = (num) => {
  if (!num) return '-';
  return Intl.NumberFormat('en-US', { notation: 'compact' }).format(num);
};

const Home = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [results, setResults] = useState([]);
	const [totalPages, setTotalPages] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [articles, setArticles] = useState([]);
	const [homepagedata, setHomepagedata] = useState([]);
	const [trendingCoins, setTrendingCoins] = useState([]);
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setToplosers] = useState([]);
  const [marketCap, setMarketCap] = useState(null);
  const [marketCapChange, setMarketCapChange] = useState(null);
  const [volume, setVolume] = useState(null);
  const [marketCapArray, setMarketCapArray] = useState([]);
  const [volumeArray, setVolumeArray] = useState([]);
  const [volumeChange, setVolumeChange] = useState(null);
  const [percentages, setPercentages] = useState({
    yesterday: null,
    lastWeek: null,
    lastMonth: null,
  });

  	useEffect(() => {
  		fetchHomepagedata();
	  }, []);


		const fetchResults = async () => {
	    const query = new URLSearchParams({ page_number: currentPage }).toString();
	    try {
	      const response = await fetch(`${apiUrl}/get-coins-data?${query}`);
	      const data = await response.json();
	      setCache(CACHE_KEY, data, CACHE_DURATION);
	      setResults(data.resluts);
	      setTotalPages(data.total_pages);     
	    } catch (error) {
	      console.error("Error fetching data:", error);
	    }
  	};

  	const fetchHomepagedata = async () => {
	    try {
	      const response1 = await fetch(`https://coinmediaworld.com/admin/api/page/1`);
	      const pageData = await response1.json();
	      // const pageData = response1.data;
        const parsedContent = typeof pageData.content === 'string'
          ? JSON.parse(pageData.content)
          : pageData.content;

        setHomepagedata(parsedContent);
        setLoading(false);
        // 1. Global Market Cap & Volume Data
	      const res = await fetch(`${apiUrl}/get-global-data`);
	      const { data } = await res.json();

	      setMarketCap(data.total_market_cap.usd);
	      setMarketCapChange(data.market_cap_change_percentage_24h_usd);
	      setVolume(data.total_volume.usd);

	      // 2. Market Cap & Volume History (Bitcoin as reference)
	      const chartRes = await fetch(`${apiUrl}/get-chartres-data`);
	      const chartData = await chartRes.json();

	      // Extract values
	      const marketCaps = chartData.market_caps.map((entry) => entry[1]);
	      const volumes = chartData.total_volumes.map((entry) => entry[1]);

	      // Downsample to stay under QuickChart 288 point limit
	      const downsample = (arr, step = 6) => arr.filter((_, i) => i % step === 0);

	      setMarketCapArray(downsample(marketCaps));
	      setVolumeArray(downsample(volumes));

	      setVolumeArray(downsample(volumes));

	      // Calculate % change for volume
	      const volStart = volumes[0];
	      const volEnd = volumes[volumes.length - 1];
	      const volChange = ((volEnd - volStart) / volStart) * 100;
	      setVolumeChange(volChange);

        // Fetch trending coins
	      const trendingResponse = await fetch(`${apiUrl}/get-trendingcoin-data`);
	      const trendingData = await trendingResponse.json();
	      
	      // Fetch top gainers
	      const gainersResponse = await fetch(`${apiUrl}/get-topgainers-data`);
	      const gainersData = await gainersResponse.json();

	      setTrendingCoins(trendingData.coins.slice(0, 5));
	      setTopGainers(gainersData);
	      
	    } catch (error) {
	      console.error("Error fetching data:", error);
	    }
  	};

  	useEffect(() => {
    const fetchTopLosers = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/coins/top-losers`);
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }
        
        let data = await response.json();
        
        // Handle API errors
        if (data.error) {
          throw new Error(data.error);
        }
        
        // Safely process and sort data
        data = data
          .map(coin => ({
            ...coin,
            price_change_percentage_24h: coin.price_change_percentage_24h || 0,
            market_cap: coin.market_cap || 0,
            current_price: coin.current_price || 0
          }))
          .filter(coin => coin.price_change_percentage_24h < 0)
          .sort((a, b) => {
            // Safe comparison with fallback to 0
            const aChange = a.price_change_percentage_24h || 0;
            const bChange = b.price_change_percentage_24h || 0;
            return aChange - bChange;
          });
        
        setToplosers(data.slice(0, 5));
      } catch (err) {
        console.error('Error fetching top losers:', err);
      } finally {
        
      }
    };

    fetchTopLosers();
    
    // Optional: Refresh data every 5 minutes
    const interval = setInterval(fetchTopLosers, 300000);
    return () => clearInterval(interval);
  }, []);

  	useEffect(() => {
	    const cached = getCache(CACHE_KEY);
	    if (cached && !cached.isStale) {
	    	console.log('===========================if')
				setResults(cached.data.resluts);
				setTotalPages(cached.data.total_pages);
	    } else {
				fetchResults();
				console.log('===========================else')
	    }
  	}, [currentPage]);

  	useEffect(() => {
	    const fetchRSS = async () => {
	      try {
	        const rssUrl = 'https://decrypt.co/feed';
	        const proxyUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

	        const response = await fetch(proxyUrl);
	        const data = await response.json();    
	        if(data.items) {
	        	setArticles(data.items);
	        }
	      } catch (err) {
	        console.error('Error fetching RSS feed:', err);
	      }
	    };
	    fetchRSS();
	}, []);

	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true
		});
	};

	const bgClasses = [
		"yellow_bg",
	  "green_bg",
	  "bluepurple_bg",
	  "red_bg",
	  "blue_bg",
	  "purple_bg",
	  "greenblue_bg",
	  "bluegreen_bg"
	];


return (
<>
	<HelmetProvider>
		<title>Bitcoin Price Alert</title>
		<meta name="description" content="BTC is breaking resistance! 🚀 Check the live chart now." />
		<meta name="image" content=	"https://img.decrypt.co/insecure/rs:fill:1024:512:1:0/plain/https://cdn.decrypt.co/wp-content/uploads/2025/05/tether-usdt-decrypt-style-01-gID_7.png" />
		<meta name="url" content="https://crypto.snvdev.com/" />
	</HelmetProvider>
	<div className="home_wrapper">			
		<div className="home_bannersec">
			<div className="container">
				<div className="row">
					<div className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
						<div className="contntbox">
						{/*<div className="livetradingsignal">
								<span className="pulsedot"></span>
								<span>Live Trading Signals Active</span>
						</div>*/}
							
							<div className="titlecontntbox">
								<h1 dangerouslySetInnerHTML={{ __html: homepagedata.banner?.title || '' }} />
								<p>{homepagedata.banner?.content}</p>
								
								<div className="btn_mainsec">
									<a className="btn_style" target="_blank" href={homepagedata.banner?.btn_link}>{homepagedata.banner?.btn_text}</a>
									<a className="btn_style borderbtn" target="_blank" href="https://t.me/coinviews144">Join Free Telegram</a>
								</div>
							</div>
							
							<div className="traders_infobox">
								<div className="boxsec">
									<div className="icontxt">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-users h-6 w-6 text-green-400 mr-2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
										
										<div className="numbertxt">{homepagedata.our_performance?.active_traders}</div>
									</div>
									
									<div className="nametxt">Active Traders</div>
								</div>
								
								<div className="boxsec">
									<div className="icontxt">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-award h-6 w-6 text-green-400 mr-2"><circle cx="12" cy="8" r="6"></circle><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path></svg>
										
										<div className="numbertxt">{homepagedata.our_performance?.success_rate}</div>
									</div>
									
									<div className="nametxt">Success Rate</div>
								</div>
								
								<div className="boxsec">
									<div className="icontxt">
										<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-zap h-6 w-6 text-green-400 mr-2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon></svg>
										
										<div className="numbertxt">{homepagedata.our_performance?.daily_signals}</div>
									</div>
									
									<div className="nametxt">Daily Signals</div>
								</div>
							</div>
						</div>
					</div>
					
					<div className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
						<div className="livesignaldash_box">
							<div className="title_box">
								<h4>Signals</h4>
								
								{/*<span className="pulsebox">
									<span className="pulsedot"></span>
									<span>LIVE</span>
								</span>*/}
							</div>
							
							<div className="signaldashboard_mainbox">
								{homepagedata.signals?.coin_name.map((coin: string, index: number) => (
								<div key={index} className={`boxsec ${
									    index === 1 ? 'bluebox' : index === 2 ? 'redbox' : ''
									  }`}>
									<div className="namebuy_box">
										<h4>{coin}</h4>
										{/*
										<span className={`buybudge ${
											  index === 0 ? 'green' : index === 1 ? 'blue' : index === 2 ? 'red' : ''
											}`}>
											  Buy
										</span>
										*/}
										
										<span className="datebox">{homepagedata.signals.date[index]}</span>
									</div>
									<p>Entry: {homepagedata.signals.coin_target[index]}</p>
									<span>{homepagedata.signals.profit_potential[index]}</span>
								</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		
		<div className="livecryptoprices_mainsec">
			<div className="container">
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="toptitlebox">
							<div className="titlesec">
								<h2>Live Crypto Prices</h2>
								<p>Real-time cryptocurrency market data and price movements</p>
							</div>
							
							{/*<div className="liveupdatebox">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-activity h-4 w-4 text-green-400 animate-pulse"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
								
								<span className="">Live Updates</span>
							</div>*/}
						</div>
						
						<div className="table_mainsec">
							<div className="table-responsive">
								<table className="table">
									<thead>
										<tr>
											<th>Coin</th>
											<th>Price</th>
											<th>24h Change</th>
											<th>Volume</th>
											<th>Chart</th>
										</tr>
									</thead>
									
									<tbody>
										{results.length ? (
					                        results.map((result, index) => (
					                        <tr key={result.id}>
												<td>
													<div className="coinnamebox">
														<div className="logobox"><img src={result.image} alt={result.name} /></div>
														
														<div className="coinname">
															<div className="shortname">
																<a href={`${homeUrl}coin/${result.id}`}>{result.symbol}</a>
															</div>
															<div className="fullname">{result.name}</div>
														</div>
													</div>
												</td>
												
												<td>
													<div className="">${result.current_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
												</td>
												
												<td>
													<div className="changeinfo">
														<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="green"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
														
														<span className="persentage green">{result.price_change_percentage_24h != null
														    ? result.price_change_percentage_24h.toLocaleString('en-US', {
														        minimumFractionDigits: 2,
														        maximumFractionDigits: 2
														      }) + '%'
														    : 'N/A'}</span>
													</div>
													
													<div className="price">${result.total_volume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
												</td>
												
												<td>{result.total_volume}</td>
												
												<td>
													<div className="chartgraph">
														<img alt={result.name} src={`https://www.coingecko.com/coins/${result.coin_id}/sparkline.svg`} />
													</div>
												</td>
											</tr>
											))
						                      ) : (
						                        <tr>
						                          <td colSpan="5">No result found</td>
						                        </tr>
						                      )}
									</tbody>
								</table>
							</div>
						</div>
						
						<div className="btn_mainsec">
							<a className="btn_style" href={`${homeUrl}coin-list`}>View All Coins</a>
						</div>
					</div>
				</div>
			</div>
		</div>
		
		{/*<div className="feargreedindex_mainsec">
			<div className="container">
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="title_mainsec">
							<h2>Crypto Liquidation Heatmap and Fear &amp; Greed Index</h2>
						</div>
					</div>
				</div>
				
				<div className="row">
					<div className="col-sm-12 col-md-8 col-lg-8 col-xl-8">
						<div className="liquidationheatmapbox">
							 <Heatmap />
						</div>
					</div>
					
					<div className="col-sm-12 col-md-4 col-lg-4 col-xl-4">
						<div className="feargreedbox">
							<CryptoFearGreedIndex />
						</div>
					</div>
				</div>
			</div>
	    </div>*/}
		
		<div className="dominance_mainsec">
			<div className="container">
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="title_mainsec">
							<h2>Bitcoin Dominance</h2>
							<p>Bitcoin (BTC) dominance is a metric used to measure the relative market share or dominance of Bitcoin in the overall cryptocurrency sector.</p>
						</div>
					</div>
				</div>
				
				<div className="row">
					<div className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
						<div className="dominance_innerbox">
							<BitcoinDominance />
						</div>
					</div>
					
					<div className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
						<div className="marketprice_mainsec">
							<div className="tranding_coins coinbox">
								<div className="titlebox">
									<h3>Top Gainers</h3>
								</div>
								
								<ul>
									{topGainers.map((coin) => {
										const change24h = coin.price_change_percentage_24h;
										return (
										  <li key={coin.id}>
											<div className="coinname">
											  <img 
												alt={coin.name} 
												src={coin.image} 
												onError={(e) => {
												  e.target.src = `${homeUrl}/assets/img/default-coin.png`;
												}}
											  />
											  
												<a href={`/coin/${coin.id}`} onClick={() => navigate(`/coin/${coin.id}`)}><span >{coin.name}</span></a>
											</div>
											
											<div className="pricebox">
											  <h2>
												${coin.current_price.toLocaleString('en-US', {
												  minimumFractionDigits: 2,
												  maximumFractionDigits: 6
												})}
												<span className={`updown_txt ${
												  change24h >= 0 ? 'upcoin' : 'downcoin'
												}`}>
												  <i className={`fas fa-caret-${
													change24h >= 0 ? 'up' : 'down'
												  }`}></i>
												  <span>{Math.abs(change24h).toFixed(2)}%</span>
												</span>
											  </h2>
											</div>
										  </li>
										);
									})}
								</ul>
							</div>
							
							<div className="tranding_coins coinbox">
								<div className="titlebox">
									<h3>Top Losers</h3>
								</div>
								
								<ul>
									{topLosers.map((coin) => {
										const change24h = coin.price_change_percentage_24h;
										return (
										  <li key={coin.id}>
											<div className="coinname">
											  <img 
												alt={coin.name} 
												src={coin.image} 
												onError={(e) => {
												  e.target.src = `${homeUrl}/assets/img/default-coin.png`;
												}}
											  />
											  <a href={`/coin/${coin.id}`} onClick={() => navigate(`/coin/${coin.id}`)}><span >{coin.name}</span></a>
											</div>
											
											<div className="pricebox">
											  <h2>
												${coin.current_price.toLocaleString('en-US', {
												  minimumFractionDigits: 2,
												  maximumFractionDigits: 4
												})}
												<span className={`updown_txt ${
												  change24h >= 0 ? 'upcoin' : 'downcoin'
												}`}>
												  <i className={`fas fa-caret-${
													change24h >= 0 ? 'up' : 'down'
												  }`}></i>
												  <span>{Math.abs(change24h).toFixed(2)}%</span>
												</span>
											  </h2>
											</div>
										  </li>
										);
									})}
								</ul>
							</div>
						</div>
					</div>
					
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="marketprice_mainsec">
							<div className="marketprice_volume">
								<div className="row">
									<div className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
										<div className="marketcap coinbox">
											<div className="titlebox">
												<h3>Market Cap</h3>
											</div>
											
											<div className="price_graphbox">
												<div className="pricebox">
													<h2>
														{formatNumber(marketCap)}
														<span className={`updown_txt ${
															marketCapChange >= 0 
																? 'upcoin' 
																: 'downcoin'
															}`}>
															
															<i className={`fas fa-caret-${
															  marketCapChange >= 0 
																? 'up' 
																: 'down'
															}`}></i>
															
															<span>
																{Math.abs(marketCapChange).toFixed(2)}%
															</span>
														</span>
													</h2>
												</div>
												
												<div className="graphimg" bis_skin_checked="1">
													<img
													  src={getChartUrl(marketCapArray, 'Market Cap', marketCapChange)}
													  alt="Market Cap Chart"
													  width="300"
													  height="150"
													/>
												</div>
											</div>
										</div>
									</div>
									
									<div className="col-sm-12 col-md-6 col-lg-6 col-xl-6">
										<div className="marketcap coinbox">
											<div className="titlebox">
												<h3>24h Trading Volume</h3>
											</div>
											
											<div className="price_graphbox">
												<div className="pricebox">
													<h2>272B
														<span className="updown_txt upcoin">
															<i className="fas fa-caret-up"></i>
															<span>25.17%</span>
														</span>
													</h2>
												</div>
												
												<div className="graphimg">
													<img alt="Volume Chart" width="300" height="150" src={getChartUrl(volumeArray, 'Volume', volumeChange)} />
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
	    </div>
		
		<div className="getpremium_mainsec">
			<div className="">
			 {/*
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="contntsec">
							<h3>{homepagedata.word_analysis?.heading}</h3>
							<p dangerouslySetInnerHTML={{
								    __html: homepagedata.word_analysis?.content.replace(/\n/g, '<br />'),
								  }} />
						</div>
					</div>
				</div>
				*/}
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="getpremium_signals">
							<div className="contntbox">
								<h3>{homepagedata.premium_signals?.heading}</h3>
								<p dangerouslySetInnerHTML={{
								    __html: homepagedata.premium_signals?.content.replace(/\n/g, '<br />'),
								  }} />
								<div className="btn_mainsec">
									<a target="_blank" href={homepagedata.premium_signals?.btn_link} className="btn_style borderbtn">
									  {homepagedata.premium_signals?.btn_text}
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		
		<div className="news_mainsec livecryptoprices_mainsec">
			<div className="container">
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="toptitlebox">
							<div className="titlesec">
								<h2>Latest Crypto News</h2>
								<p>Stay updated with breaking news and market-moving events</p>
							</div>
							
							<div className="btn_mainsec">
								<a className="btn_style" href={`${homeUrl}news`}>
									View All News
									
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-external-link h-4 w-4"><path d="M15 3h6v6"></path><path d="M10 14 21 3"></path><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path></svg>
								</a>
							</div>
						</div>
					</div>
				</div>
				
				<div className="row">
					{articles.slice(0, 6).map((item, index) => (
						<div className="col-sm-12 col-md-6 col-lg-4 col-xl-4">
							<div className="articals_mainbox">
								<div className="articals_innerbox">
									<div className="articals_imgbox">
										<img alt={item.title} src={item.enclosure.link} />
									</div>
									
									<div className="articals_contntbox">
										<div className="artical_contnt">
											<h3><a href={`${homeUrl}artical/${item.title.replace(/[^a-zA-Z0-9 ]/g, '')}?source=Decrypt`}>{item.title}</a></h3>
											<p className="article_description" dangerouslySetInnerHTML={{ __html: item.description }}></p>
										</div>
										
										<div className="artical_info">
											<div className="datetimebox">
												<span className="datetime">
													<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-calendar h-3 w-3"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
													
													<span className="datetxt">{formatDate(item.pubDate)}</span>
												</span>
												{/*
												<span className="datetime">
													<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-clock h-3 w-3"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
													
													<span className="timetxt">1/15/2024</span>
												</span>
												*/}
											</div>
											
											<div className="trendingtxt">
												<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-trending-up h-3 w-3"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
												
												<span>Trending</span>
											</div>
										</div>
									</div>
									
									<div className="share_social_sec">
										<span className="titletxt">Share:</span>
										<ul>
											<li>
												<a
												  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${homeUrl}artical/${item.title}`)}`}
												  target="_blank"
												  rel="noopener noreferrer"
												><i className="fab fa-facebook-f"></i>
											</a>
											</li>
											<li>
												<a
												  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${homeUrl}artical/${item.title}`)}`}
												  target="_blank"
												  rel="noopener noreferrer"
												><i className="fab fa-linkedin-in"></i>
												</a>
											</li>
											<li>
												<a
												  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(`${homeUrl}artical/${item.title}`)}&text=${encodeURIComponent(item.title)}`}
												  target="_blank"
												  rel="noopener noreferrer"
												><i className="fab fa-twitter"></i>
												</a>
											</li>
											<li>
												<a
												  href={`https://t.me/share/url?url=${encodeURIComponent(`${homeUrl}artical/${item.title}`)}&text=${encodeURIComponent(item.title)}`}
												  target="_blank"
												  rel="noopener noreferrer"
												><i class="fab fa-telegram-plane"></i>
												</a>
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
		
		<div className="whychoose_mainsec">
			<div className="container">
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="toptitlebox">
							<h2>Why Choose Coinviews?</h2>
							<p>Professional trading tools and signals designed to help you make profitable decisions in the cryptocurrency market.</p>
						</div>
					</div>
				</div>
				
				<div className="row">
					{homepagedata.why_choose?.heading.map((title: string, i: number) => (
						<div key={i} className="col-sm-12 col-md-4 col-lg-3 col-xl-3">
							<div className="whychoose_contntbox">
								<div className={`iconbox ${bgClasses[i % bgClasses.length]}`}>
									<img src={`${adminUrl}/public/storage/${homepagedata.why_choose.icon[i]}`} alt={title} />
								</div>
								
								<div className="contntbox">
									<h3>{title}</h3>
									<p>{homepagedata.why_choose.description[i]}</p>
								</div>
							</div>
						</div>
					))}
				</div>
				
				<div className="row">
					<div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
						<div className="ourperformance_sec">
							<div className="titlebox">
								<h3>Our Performance</h3>
								<p>Verified results from our trading signals</p>
							</div>
							
							<div className="traders_infobox">
								<div className="boxsec">
									<div className="icontxt">
										<div className="numbertxt greentxt">{homepagedata.our_performance?.success_rate}</div>
									</div>
									
									<div className="nametxt">Success Rate</div>
								</div>
								
								<div className="boxsec">
									<div className="icontxt">
										<div className="numbertxt bluetxt">{homepagedata.our_performance?.active_traders}</div>
									</div>
									
									<div className="nametxt">Active Traders</div>
								</div>
								
								<div className="boxsec">
									<div className="icontxt">
										<div className="numbertxt purpletxt">{homepagedata.our_performance?.profits_generated}</div>
									</div>
									
									<div className="nametxt">Profits Generated</div>
								</div>
								
								<div className="boxsec">
									<div className="icontxt">
										<div className="numbertxt yellowtxt">{homepagedata.our_performance?.daily_signals}</div>
									</div>
									
									<div className="nametxt">Daily Signals</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	{loading && (
		<div className="loader_mainbox">
			<div className="loader_mainsec">
				<div className="loader market-spinner">
					<svg class="icon" viewBox="0 0 24 24" width="20" height="20" aria-label="Loading market data">
					<polyline points="22 7 13.5 15.5 8.5 10.5 2 17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					<polyline points="16 7 22 7 22 13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</div>
					 
				<div className="text-loader">
					<span>C</span>
					<span>O</span>
					<span>I</span>
					<span>N</span>
					<span>V</span>
					<span>I</span>
					<span>E</span>
					<span>W</span>
					<span>S</span>
				</div>
			</div>
		</div>
	)}
</>
);
};

export default Home;